import { computed, onBeforeUnmount, ref, unref, watch } from 'vue'
import { analyzeReplay } from '@/api/meetingReplay'

export const replayLanguageKey = (language) => {
  const key = String(language || '')
    .replace(/_/g, '-')
    .toLowerCase()
  return key === 'zh-cn' ? 'zh-hans' : key
}

// Intersection only: removing pauses must never restore rehearsals outside the chosen ranges.
export const intersectCaptureRanges = (ranges, captures) => {
  const pieces = ranges
    .flatMap((range) =>
      captures.map((capture) => ({
        startAtMs: Math.max(range.startAtMs, capture.startAtMs),
        endAtMs: Math.min(range.endAtMs, capture.endAtMs),
      }))
    )
    .filter((range) => range.endAtMs > range.startAtMs)
    .sort((a, b) => a.startAtMs - b.startAtMs)
  return pieces.reduce((merged, range) => {
    const last = merged[merged.length - 1]
    if (last && range.startAtMs <= last.endAtMs) last.endAtMs = Math.max(last.endAtMs, range.endAtMs)
    else merged.push(range)
    return merged
  }, [])
}

export function useReplayRangeAnalysis(meetingCode, request, enabled) {
  const result = ref(null),
    error = ref(''),
    running = ref(false),
    resolvedKey = ref('')
  let sequence = 0,
    timer = null,
    stopped = false
  const scope = computed(() => {
    const value = unref(request),
      meeting = unref(meetingCode)
    if (
      !unref(enabled) ||
      !meeting ||
      !value?.ranges?.length ||
      !value.ranges.every((range) => range.startAtMs > 0 && range.endAtMs > range.startAtMs)
    )
      return ''
    return JSON.stringify({
      meeting,
      request: {
        ranges: value.ranges,
        pausePolicy: value.pausePolicy || 'PRESERVE',
        sourceSelectionMode: value.sourceSelectionMode,
        captureSessionIds: value.sourceSelectionMode === 'EXPLICIT' ? value.captureSessionIds : [],
        generationIntent: 'PARTIAL',
        languages: [],
        inheritPreviousPpt: value.inheritPreviousPpt,
      },
    })
  })
  const analysis = computed(() => (resolvedKey.value === scope.value ? result.value : null))
  const loading = computed(() => Boolean(scope.value) && running.value)
  const refresh = async () => {
    const key = scope.value,
      current = ++sequence
    clearTimeout(timer)
    error.value = ''
    result.value = null
    if (!key || stopped) {
      running.value = false
      return
    }
    running.value = true
    const payload = JSON.parse(key)
    try {
      const response = await analyzeReplay(payload.meeting, payload.request)
      if (stopped || current !== sequence || scope.value !== key) return
      if (![0, 200, '0', '200'].includes(response?.code) || !response.data)
        throw new Error(response?.msg || response?.message || '范围分析失败')
      result.value = response.data
      resolvedKey.value = key
    } catch (e) {
      if (!stopped && current === sequence && scope.value === key) error.value = e.message || '范围分析失败，请重试'
    } finally {
      if (!stopped && current === sequence && scope.value === key) running.value = false
    }
  }
  watch(
    scope,
    (key) => {
      ++sequence
      clearTimeout(timer)
      result.value = null
      error.value = ''
      running.value = Boolean(key)
      if (key) timer = setTimeout(refresh, 300)
    },
    { immediate: true, flush: 'sync' }
  )
  onBeforeUnmount(() => {
    stopped = true
    ++sequence
    clearTimeout(timer)
  })
  return { analysis, loading, error, refresh }
}
